import React, { useState, useEffect } from "react";
import { Table, Typography, Card, Button, Spin, Input, Select } from "antd";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  PrinterOutlined,
  DownloadOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import { Dropdown, ButtonGroup } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import Login from "../../Authentication/Login";
import AccontingReportTabs from "./AccoutingReportTabs";
import Header from "../../Common/Layout/Header";

const { Text, Title } = Typography;
const { Option } = Select;

const LedgerReport = () => {
  const { user, loading } = useAuth();
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [accountFilter, setAccountFilter] = useState(null);
  const [accountsList, setAccountsList] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);
  const [loadingReport, setLoadingReport] = useState(false);

  // Fetch company data
  const getCompanyData = async () => {
    try {
      const res = await apiService.get(`${config.GETCOMPANYBYUSER}/${user.id}`);
      setCompanyData(res);
    } catch (err) {
      console.error("Error fetching company data:", err);
    }
  };

  // Fetch accounts list
  const getAccountsList = async () => {
    try {
      const res = await apiService.getAll(`${config.GETACCOUNTS}/${user.id}`);
      setAccountsList(res || []);
    } catch (err) {
      console.error("Error fetching accounts:", err);
    }
  };

  // Fetch general ledger data
  const getGeneralLedger = async () => {
    setLoadingReport(true);
    try {
      const params = new URLSearchParams();
      if (fromDate)
        params.append("FromDate", moment(fromDate).format("YYYY-MM-DD"));
      if (toDate) params.append("ToDate", moment(toDate).format("YYYY-MM-DD"));
      if (accountFilter) params.append("AccountId", accountFilter);

      const res = await apiService.getAll(
        `${config.GENERALLEDGERREPORT}/${user.id}?${params.toString()}`
      );

      // Calculate opening and closing balances
      const opening = res?.openingBalance || 0;
      const closing = res?.closingBalance || 0;

      setReportData(res?.transactions || []);
      setOpeningBalance(opening);
      setClosingBalance(closing);
    } catch (err) {
      console.error("Error fetching general ledger:", err);
      setReportData([]);
      setOpeningBalance(0);
      setClosingBalance(0);
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (user && !loading) {
      getCompanyData();
      getAccountsList();
    }
  }, [user, loading]);

  useEffect(() => {
    if (accountFilter) {
      getGeneralLedger();
    }
  }, [accountFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    getGeneralLedger();
  };

  const handleClear = () => {
    setFromDate(moment().startOf("month").toDate());
    setToDate(moment().endOf("month").toDate());
    setAccountFilter(null);
    setReportData([]);
    setOpeningBalance(0);
    setClosingBalance(0);
  };

  const exportToPDF = () => {
    if (!accountFilter) return;

    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    // Set styles for the document
    doc.setFont("helvetica");
    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.setFont(undefined, "bold");

    // Title - centered
    doc.text("GENERAL LEDGER", 105, 20, { align: "center" });

    // Company details - centered
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.setFont(undefined, "normal");
    doc.text(companyData?.companyName || "Company", 105, 30, {
      align: "center",
    });

    const addressLine = `${companyData?.address || ""}, ${
      companyData?.city || ""
    }`;
    doc.text(addressLine, 105, 35, { align: "center" });

    const contactLine = `Phone: ${companyData?.phoneNumber || ""}`;
    doc.text(contactLine, 105, 40, { align: "center" });

    // Account information
    const selectedAccount = accountsList.find((a) => a.id === accountFilter);
    doc.text(
      `Account: ${selectedAccount?.code || ""} - ${
        selectedAccount?.name || ""
      }`,
      105,
      45,
      { align: "center" }
    );

    // Report period
    doc.text(
      `From ${moment(fromDate).format("MMM DD, YYYY")} to ${moment(
        toDate
      ).format("MMM DD, YYYY")}`,
      105,
      50,
      { align: "center" }
    );

    // Generated date
    doc.text(
      `Generated on: ${moment().format("YYYY-MM-DD hh:mm A")}`,
      105,
      55,
      { align: "center" }
    );

    // Opening balance
    doc.setFontSize(12);
    doc.text(
      `Opening Balance: ${openingBalance >= 0 ? "DR" : "CR"} ${Math.abs(
        openingBalance
      ).toFixed(2)}`,
      14,
      65
    );

    // General Ledger table
    doc.autoTable({
      startY: 70,
      head: [
        ["Date", "Reference", "Description", "Debit", "Credit", "Balance"],
      ],
      body: reportData.map((entry) => [
        moment(entry.date).format("MM/DD/YYYY"),
        entry.reference,
        entry.description,
        entry.debit ? entry.debit.toFixed(2) : "",
        entry.credit ? entry.credit.toFixed(2) : "",
        `${entry.balance >= 0 ? "DR" : "CR"} ${Math.abs(entry.balance).toFixed(
          2
        )}`,
      ]),
      foot: [
        [
          { content: "Closing Balance:", styles: { fontStyle: "bold" } },
          "",
          "",
          "",
          "",
          {
            content: `${closingBalance >= 0 ? "DR" : "CR"} ${Math.abs(
              closingBalance
            ).toFixed(2)}`,
            styles: { fontStyle: "bold" },
          },
        ],
      ],
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
        halign: "left",
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 25 },
        2: { cellWidth: 50 },
        3: { cellWidth: 20, halign: "right" },
        4: { cellWidth: 20, halign: "right" },
        5: { cellWidth: 25, halign: "right" },
      },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
      },
    });

    doc.save(`General_Ledger_${selectedAccount?.code || "Account"}.pdf`);
  };

  const handlePrint = () => {
    if (!accountFilter) return;

    const selectedAccount = accountsList.find((a) => a.id === accountFilter);
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>General Ledger - ${selectedAccount?.code || ""} - ${
      selectedAccount?.name || ""
    }</title>
          <style>
            body { font-family: Arial; margin: 0; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .company-name { font-size: 18px; font-weight: bold; }
            .company-info { font-size: 12px; margin-bottom: 5px; }
            .report-title { font-size: 16px; font-weight: bold; margin: 10px 0; }
            .account-info { font-size: 14px; margin-bottom: 5px; }
            .report-period { font-size: 12px; margin-bottom: 15px; }
            .opening-balance { font-size: 12px; margin: 10px 0; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; }
            th { background-color: #2980b9; color: white; padding: 6px; text-align: left; }
            td { padding: 5px; border-bottom: 1px solid #ddd; }
            .text-right { text-align: right; }
            .total-row { font-weight: bold; }
            .footer { margin-top: 20px; font-size: 10px; text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">${
              companyData?.companyName || "Company"
            }</div>
            <div class="company-info">
              ${companyData?.address || ""}, ${companyData?.city || ""}<br>
              Phone: ${companyData?.phoneNumber || ""}
            </div>
            <div class="report-title">GENERAL LEDGER</div>
            <div class="account-info">
              Account: ${selectedAccount?.code || ""} - ${
      selectedAccount?.name || ""
    }
            </div>
            <div class="report-period">
              From ${moment(fromDate).format("MMM DD, YYYY")} to ${moment(
      toDate
    ).format("MMM DD, YYYY")}<br>
              Generated on: ${moment().format("MMM DD, YYYY hh:mm A")}
            </div>
          </div>

          <div class="opening-balance">
            Opening Balance: ${openingBalance >= 0 ? "DR" : "CR"} ${Math.abs(
      openingBalance
    ).toFixed(2)}
          </div>

          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Reference</th>
                <th>Description</th>
                <th class="text-right">Debit</th>
                <th class="text-right">Credit</th>
                <th class="text-right">Balance</th>
              </tr>
            </thead>
            <tbody>
              ${reportData
                .map(
                  (entry) => `
                <tr>
                  <td>${moment(entry.date).format("MM/DD/YYYY")}</td>
                  <td>${entry.reference}</td>
                  <td>${entry.description}</td>
                  <td class="text-right">${
                    entry.debit ? entry.debit.toFixed(2) : ""
                  }</td>
                  <td class="text-right">${
                    entry.credit ? entry.credit.toFixed(2) : ""
                  }</td>
                  <td class="text-right">${
                    entry.balance >= 0 ? "DR" : "CR"
                  } ${Math.abs(entry.balance).toFixed(2)}</td>
                </tr>
              `
                )
                .join("")}
              <tr class="total-row">
                <td colspan="3">Closing Balance</td>
                <td class="text-right"></td>
                <td class="text-right"></td>
                <td class="text-right">${
                  closingBalance >= 0 ? "DR" : "CR"
                } ${Math.abs(closingBalance).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            © ${new Date().getFullYear()} ${
      companyData?.companyName || "Company"
    }. All rights reserved.
          </div>

          <script>
            setTimeout(() => {
              window.print();
              window.close();
            }, 200);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) return <p>Loading...</p>;
  if (!user) return <Login></Login>;

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <AccontingReportTabs pageName="LEDGERREPORT" />
                </div>
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-8">
                      <div className="d-flex align-items-end flex-wrap">
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">From Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={fromDate}
                              onChange={(date) => setFromDate(date)}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select from date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="date-picker-group me-2 mb-2">
                          <label className="form-label">To Date:</label>
                          <div className="position-relative">
                            <DatePicker
                              selected={toDate}
                              onChange={(date) => setToDate(date)}
                              minDate={fromDate}
                              dateFormat="dd-MMM-yyyy"
                              className="form-control pe-4"
                              placeholderText="Select to date"
                              showIcon
                              icon={
                                <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                              }
                            />
                          </div>
                        </div>
                        <div className="account-select-group me-2 mb-2">
                          <label className="form-label">Account:</label>
                          <Select
                            showSearch
                            style={{ width: 250 }}
                            placeholder="Select account"
                            optionFilterProp="children"
                            value={accountFilter}
                            onChange={setAccountFilter}
                            filterOption={(input, option) =>
                              option.children
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                            }
                          >
                            {accountsList.map((account) => (
                              <Option key={account.id} value={account.id}>
                                {account.code} - {account.name}
                              </Option>
                            ))}
                          </Select>
                        </div>
                        <div className="d-flex mb-2">
                          <Button
                            variant="primary"
                            className="me-2"
                            onClick={handleSearch}
                            disabled={!accountFilter}
                          >
                            <SearchOutlined /> Search
                          </Button>
                          <Button variant="danger" onClick={handleClear}>
                            <ReloadOutlined /> Clear
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 d-flex justify-content-end">
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle
                          variant="primary"
                          id="dropdown-export"
                          disabled={!accountFilter}
                        >
                          <DownloadOutlined /> Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={exportToPDF}>
                            PDF
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <Button
                        variant="secondary"
                        onClick={handlePrint}
                        className="ms-2"
                        disabled={!accountFilter}
                      >
                        <PrinterOutlined /> Print
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  <div className="report-container">
                    <div className="header-section">
                      <h1 className="company-name">
                        {companyData?.companyName || "Company"}
                      </h1>
                      <p className="company-info">
                        {companyData?.address || ""}, {companyData?.city || ""}
                        <br />
                        Phone: {companyData?.phoneNumber || ""}
                      </p>
                    </div>

                    <div className="report-title">
                      <h2>General Ledger</h2>
                      {accountFilter && (
                        <>
                          <p className="account-info">
                            Account:{" "}
                            {accountsList.find((a) => a.id === accountFilter)
                              ?.code || ""}{" "}
                            -{" "}
                            {accountsList.find((a) => a.id === accountFilter)
                              ?.name || ""}
                          </p>
                          <p className="report-period">
                            From {moment(fromDate).format("MMM DD, YYYY")} to{" "}
                            {moment(toDate).format("MMM DD, YYYY")}
                          </p>
                        </>
                      )}
                    </div>

                    {accountFilter ? (
                      <>
                        <div className="opening-balance mb-2">
                          <Text strong>Opening Balance: </Text>
                          <Text
                            type={openingBalance >= 0 ? "success" : "danger"}
                          >
                            {openingBalance >= 0 ? "DR" : "CR"}{" "}
                            {Math.abs(openingBalance).toFixed(2)}
                          </Text>
                        </div>

                        <Spin spinning={loadingReport}>
                          <div className="table-responsive">
                            <table className="table table-bordered table-hover mb-0">
                              <thead className="bg-soft-primary bg-gradient">
                                <tr>
                                  <th>Date</th>
                                  <th>Reference</th>
                                  <th>Description</th>
                                  <th className="text-end">Debit</th>
                                  <th className="text-end">Credit</th>
                                  <th className="text-end">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {reportData.length > 0 ? (
                                  reportData.map((entry, idx) => (
                                    <tr key={`entry-${idx}`}>
                                      <td>
                                        {moment(entry.date).format(
                                          "MMM DD, YYYY"
                                        )}
                                      </td>
                                      <td>{entry.reference}</td>
                                      <td>{entry.description}</td>
                                      <td className="text-end">
                                        {entry.debit
                                          ? entry.debit.toFixed(2)
                                          : ""}
                                      </td>
                                      <td className="text-end">
                                        {entry.credit
                                          ? entry.credit.toFixed(2)
                                          : ""}
                                      </td>
                                      <td className="text-end">
                                        <Text
                                          type={
                                            entry.balance >= 0
                                              ? "success"
                                              : "danger"
                                          }
                                        >
                                          {entry.balance >= 0 ? "DR" : "CR"}{" "}
                                          {Math.abs(entry.balance).toFixed(2)}
                                        </Text>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan="6"
                                      className="text-center text-muted"
                                    >
                                      No transactions found
                                    </td>
                                  </tr>
                                )}
                                <tr className="total-row">
                                  <td colSpan="3">Closing Balance</td>
                                  <td className="text-end"></td>
                                  <td className="text-end"></td>
                                  <td className="text-end">
                                    <Text
                                      strong
                                      type={
                                        closingBalance >= 0
                                          ? "success"
                                          : "danger"
                                      }
                                    >
                                      {closingBalance >= 0 ? "DR" : "CR"}{" "}
                                      {Math.abs(closingBalance).toFixed(2)}
                                    </Text>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </Spin>
                      </>
                    ) : (
                      <div className="text-center text-muted py-4">
                        Please select an account to view the general ledger
                      </div>
                    )}

                    <div className="report-footer mt-3">
                      <p className="mb-1">
                        This report was generated automatically on{" "}
                        {moment().format("MMM DD, YYYY")}.
                      </p>
                      <p className="mb-0">
                        © {new Date().getFullYear()}{" "}
                        {companyData?.companyName || "Company"}. All rights
                        reserved.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LedgerReport;
